import { useQuery } from '@tanstack/react-query';
import { Suspense, useMemo, useState } from 'react';

import { BarCompare, LineTrend } from '../../components/charts';
import { ExportMenu } from '../../components/ExportMenu';
import { KpiCard } from '../../components/KpiCard';
import { Modal } from '../../components/Modal';
import { DataTable } from '../../components/Table';
import {
  getBranchDetail,
  getSalesByBranch,
  listBranches,
} from '../../data/fetchers';
import { CHART_H } from '../../lib/chartTokens';
import type { ExportCol } from '../../lib/export';
import {
  fmtAmount,
  fmtBaht,
  fmtBahtShort,
  fmtPct,
  fmtSatangCompact,
} from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';
import { useDateRange } from '../../lib/useDateRange';
import { ReportDateFilter } from './ReportDateFilter';
import { ReportHeader } from './ReportHeader';

const deltaFrac = (cur: number, prev: number): number =>
  prev > 0 ? (cur - prev) / prev : cur > 0 ? 1 : 0;
const round1 = (n: number) => Math.round(n * 10) / 10;
const chartSkeleton = (
  <div
    className="animate-pulse bg-bg-warm rounded-card"
    style={{ height: CHART_H.md }}
  />
);

interface ExportRow {
  rank: number;
  branch: string;
  revenueBaht: string;
  revenueSatang: number;
  sharePct: number;
  deltaPct: number;
  perDayBaht: string;
  perDaySatang: number;
}
const exportCols: ExportCol<ExportRow>[] = [
  { key: 'rank', header: 'อันดับ' },
  { key: 'branch', header: 'สาขา' },
  { key: 'revenueBaht', header: 'ยอดขาย' },
  { key: 'revenueSatang', header: 'ยอดขาย (satang)' },
  { key: 'sharePct', header: 'สัดส่วน %' },
  { key: 'deltaPct', header: 'Δ %' },
  { key: 'perDayBaht', header: 'เฉลี่ย/วัน' },
  { key: 'perDaySatang', header: 'เฉลี่ย/วัน (satang)' },
];

export function SalesByBranchView() {
  const { scope, ready } = useBranchScope();
  const { range, prev } = useDateRange();
  const [openId, setOpenId] = useState<string | null>(null);
  const { data: branches = [] } = useQuery({
    queryKey: ['branches'],
    queryFn: listBranches,
  });
  const shortOf = useMemo(() => {
    const m = new Map(branches.map((b) => [b.id, b.short]));
    return (id: string) => m.get(id) ?? id;
  }, [branches]);

  const { data, isLoading } = useQuery({
    queryKey: [
      'report',
      'by-branch',
      scope.branchId,
      scope.allowedBranchIds,
      range.fromDay,
      range.toDay,
    ],
    queryFn: () => getSalesByBranch(scope, range, prev),
    enabled: ready,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const days = data?.days ?? 1;
  const totalDelta = deltaFrac(total, data?.prevTotal ?? 0);
  const top = rows[0];

  const exportRows: ExportRow[] = rows.map((r, i) => ({
    rank: i + 1,
    branch: shortOf(r.branchId),
    revenueBaht: fmtBaht(r.revenue),
    revenueSatang: r.revenue,
    sharePct: total ? round1((r.revenue / total) * 100) : 0,
    deltaPct: round1(deltaFrac(r.revenue, r.prevRevenue) * 100),
    perDayBaht: fmtBaht(Math.round(r.revenue / days)),
    perDaySatang: Math.round(r.revenue / days),
  }));

  return (
    <div className="bo-fade">
      <ReportHeader
        title="ยอดขายแยกสาขา"
        subtitle="เปรียบเทียบสาขา · สัดส่วน · การเติบโต และเจาะลึกรายสาขา"
        status={{
          label: !ready || isLoading ? 'กำลังประมวลผล' : `${rows.length} สาขา`,
          tone: !ready || isLoading ? 'neutral' : 'ok',
        }}
        actions={
          <ExportMenu
            rows={exportRows}
            cols={exportCols}
            filename="ยอดขายแยกสาขา"
            title="ยอดขายแยกสาขา"
            pdf
          />
        }
      />
      <ReportDateFilter />

      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <KpiCard
          label="ยอดขายรวม"
          value={fmtBaht(total)}
          tone="primary"
          delta={totalDelta}
          sub="เทียบงวดก่อน"
        />
        <KpiCard
          label="จำนวนสาขา"
          value={String(rows.length)}
          tone="accent"
          sub="ในขอบเขตที่ดูได้"
        />
        <KpiCard
          label="สาขาสูงสุด"
          value={top ? shortOf(top.branchId) : '—'}
          tone="ok"
          sub={top ? fmtBahtShort(top.revenue) : undefined}
        />
      </div>

      <section aria-labelledby="branch-ranking-heading" className="min-w-0">
        <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h2
              id="branch-ranking-heading"
              className="text-h3 font-bold text-ink"
            >
              อันดับยอดขายรายสาขา
            </h2>
            <p className="mt-1 text-sm text-ink-3">
              เรียงจากยอดขายสูงสุด กดชื่อสาขาเพื่อดูแนวโน้มและสินค้าขายดี
            </p>
          </div>
          {rows.length > 0 && (
            <p className="text-sm-label text-ink-3">
              เฉลี่ยต่อสาขา {fmtBaht(rows.length ? total / rows.length : 0)}
            </p>
          )}
        </div>
        {!ready || isLoading ? (
          <div className="bo-card h-60 animate-pulse" />
        ) : (
          <DataTable
            pageSize={Number.MAX_SAFE_INTEGER}
            mobileCards
            data={rows}
            onRowClick={(row) => setOpenId(row.branchId)}
            columns={[
              { id: 'rank', header: '#', cell: ({ row }) => row.index + 1 },
              {
                id: 'branch',
                header: 'สาขา',
                cell: ({ row }) => shortOf(row.original.branchId),
              },
              {
                accessorKey: 'revenue',
                header: 'ยอดขาย',
                cell: ({ row }) => fmtAmount(row.original.revenue),
              },
              {
                id: 'share',
                header: 'สัดส่วน',
                cell: ({ row }) =>
                  fmtPct(total ? (row.original.revenue / total) * 100 : 0),
              },
              {
                id: 'delta',
                header: 'Δ เทียบงวดก่อน',
                cell: ({ row }) =>
                  fmtPct(
                    deltaFrac(row.original.revenue, row.original.prevRevenue) *
                      100,
                  ),
              },
              {
                id: 'avg',
                header: 'เฉลี่ย/วัน',
                cell: ({ row }) => fmtAmount(row.original.revenue / days),
              },
            ]}
          />
        )}
      </section>
      <Modal
        open={!!openId}
        variant="drawer"
        className="!my-0"
        title="รายละเอียดสาขา"
        onClose={() => setOpenId(null)}
      >
        {openId && (
          <BranchDrilldown
            branchId={openId}
            name={shortOf(openId)}
            range={range}
          />
        )}
      </Modal>
    </div>
  );
}

function BranchDrilldown({
  branchId,
  name,
  range,
}: {
  branchId: string;
  name: string;
  range: { fromDay: number; toDay: number };
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['branch-detail', branchId, range.fromDay, range.toDay],
    queryFn: () => getBranchDetail(branchId, range),
  });
  if (isLoading || !data)
    return <div className="h-40 m-4 animate-pulse bg-bg-warm rounded-card" />;
  return (
    <div className="min-w-0">
      <div className="mb-4">
        <div className="text-sm-label font-bold text-primary">
          รายละเอียดสาขา
        </div>
        <h3 className="mt-1 break-words text-h3 font-bold text-ink">{name}</h3>
      </div>
      <div className="grid min-w-0 grid-cols-1 gap-4">
        <div className="min-w-0 rounded-card border border-line bg-surface p-3">
          <div className="mb-2 text-sm-label font-bold text-ink-3">
            แนวโน้มรายวัน
          </div>
          {data.series.length === 0 ? (
            <div className="flex h-40 items-center justify-center text-sm text-ink-3">
              ไม่มีข้อมูลแนวโน้มในช่วงนี้
            </div>
          ) : (
            <Suspense fallback={chartSkeleton}>
              <LineTrend
                data={data.series}
                height={CHART_H.sm}
                valueFormat={fmtBahtShort}
                tickFormat={fmtSatangCompact}
              />
            </Suspense>
          )}
        </div>
        <div className="min-w-0 rounded-card border border-line bg-surface p-3">
          <div className="mb-2 text-sm-label font-bold text-ink-3">
            สินค้าขายดี (ยอดเงิน)
          </div>
          {data.topProducts.length === 0 ? (
            <div className="flex h-40 items-center justify-center text-sm text-ink-3">
              ไม่มีข้อมูลสินค้าในช่วงนี้
            </div>
          ) : (
            <Suspense fallback={chartSkeleton}>
              <BarCompare
                data={data.topProducts.map((p) => ({
                  label: p.name,
                  value: p.amount,
                }))}
                height={CHART_H.sm}
                colorByIndex
                valueFormat={fmtBahtShort}
                tickFormat={fmtSatangCompact}
              />
            </Suspense>
          )}
        </div>
      </div>
    </div>
  );
}
